import { Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { money } from "@/lib/format";
import { colorForIndex } from "@/lib/colors";
import { sound } from "@/lib/sound";

const STATUS = {
  collecting: { label: "Collecting", cls: "text-[#c084fc] bg-[#a855f7]/15 border-[#a855f7]/30" },
  partially_funded: { label: "Collecting", cls: "text-[#c084fc] bg-[#a855f7]/15 border-[#a855f7]/30" },
  fully_funded_confirmed: { label: "Funded", cls: "text-[#34d399] bg-[#34d399]/12 border-[#34d399]/30" },
  fully_funded_pending_settlement: { label: "Funded", cls: "text-[#34d399] bg-[#34d399]/12 border-[#34d399]/30" },
  settled: { label: "Settled", cls: "text-[#34d399] bg-[#34d399]/12 border-[#34d399]/30" },
  closed: { label: "Closed", cls: "text-muted-foreground bg-white/5 border-white/10" },
};

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

export function MerchantTile({ bill, size = 52 }) {
  const label = (bill.merchant || bill.name || "?").trim().charAt(0).toUpperCase();
  const color = colorForIndex((bill.name || "").length + (bill.merchant || "").length);
  return (
    <div
      className="rounded-2xl grid place-items-center shrink-0 font-extrabold text-[#0b0619]"
      style={{ height: size, width: size, background: color, fontSize: size * 0.42 }}
    >
      {bill.category_emoji ? (
        <span style={{ fontSize: size * 0.5 }}>{bill.category_emoji}</span>
      ) : (
        label
      )}
    </div>
  );
}

export default function BillCard({ bill, index = 0 }) {
  const total = Number(bill.total_amount || 0);
  const confirmed = Number(bill.funded_confirmed || 0);
  const pct = total > 0 ? Math.min(100, Math.round((confirmed / total) * 100)) : 0;
  const meta = STATUS[bill.status] || { label: bill.status, cls: "text-muted-foreground bg-white/5 border-white/10" };
  const people = (bill.participants || []).length;
  const paid = (bill.participants || []).filter(
    (p) => p.contribution_status === "confirmed" || ["funded", "paid"].includes(p.status)
  ).length;
  const itemCount = (bill.items || []).length;

  const bits = [fmtDate(bill.created_at)];
  if (itemCount) bits.push(`${itemCount} item${itemCount > 1 ? "s" : ""}`);
  bits.push(`${people} ${people === 1 ? "person" : "people"}`);

  return (
    <Link
      to={`/bill/${bill.id}`}
      onClick={() => sound.play("nav")}
      data-testid={`bill-card-${bill.id}`}
      className="hp-glass hp-card-hover hp-rise rounded-3xl p-4 block"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
    >
      <div className="flex items-center gap-3">
        <MerchantTile bill={bill} />
        <div className="min-w-0 flex-1">
          <h3 className="text-[17px] font-bold text-white truncate">{bill.merchant || bill.name}</h3>
          <p className="text-[13px] text-muted-foreground truncate mt-0.5">{bits.filter(Boolean).join("  ·  ")}</p>
        </div>
        <span className={`shrink-0 px-3 py-1 rounded-full text-[13px] font-semibold border ${meta.cls}`}>
          {meta.label}
        </span>
      </div>

      <div className="mt-3 flex items-end justify-between">
        <span className="text-[30px] leading-none font-extrabold text-white">{money(total)}</span>
        <span className="text-[13px] text-muted-foreground mb-0.5">
          {paid} of {people} paid
        </span>
      </div>

      <div className="mt-3">
        <Progress value={pct} className="h-2 bg-white/[0.06]" />
        <p className="text-[13px] text-muted-foreground mt-2">
          {money(confirmed)} funded · {pct}%
        </p>
      </div>
    </Link>
  );
}
